import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { EditableText } from '@/core/EditableText.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { OutputItemMetadataLabel } from '@/tools/models/OutputItemMetadataLabel.atom';
import { OUTPUT_SECTION_BORDER_STYLES } from '@/tools/models/template.types';
import { faPencil, faQuestionCircle, faTrash } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, Icon } from '@seeqdev/qomponents';
import type {
  FeatureMappingConfig,
  FormulaItemTableOption,
  OutputBadgeEntry,
} from '@/tools/formula/FormulaItemTable.molecule';

/**
 * A row is treated as a template derived feature (and therefore opted in to the metadata annotation)
 * when the option object carries the metadata keys — even with undefined values. Template display
 * utilities always project these keys onto every option they produce; non-template callers don't, so
 * their rows render without the annotation.
 */
const isTemplateDerivedFeatureOption = (option: FormulaItemTableOption): boolean =>
  'outputItemType' in option || 'outputItemMetadata' in option;

export interface FormulaItemTableRowProps {
  option: FormulaItemTableOption;
  testId?: string;
  isOpen: boolean;
  onToggleFormula: () => void;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  newLabel?: string;
  mappingConfig?: FeatureMappingConfig;
  selectable?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (key: string, selected: boolean) => void;
  badges?: OutputBadgeEntry[];
}

export const FormulaItemTableRow = ({
  option,
  testId,
  isOpen,
  onToggleFormula,
  onEditClick,
  onRemoveClick,
  onAdditionalIconClick,
  additionalIcon,
  additionalIconTooltip,
  onSecondaryIconClick,
  secondaryIcon,
  secondaryIconTooltip,
  textEditable,
  onTextChange,
  newLabel,
  mappingConfig,
  selectable = false,
  isSelected = false,
  onSelectionChange,
  badges,
}: FormulaItemTableRowProps) => {
  const { t } = useTranslation();

  const isUnselected = selectable && !isSelected;
  const showNewLabel = !!newLabel && (option.isNew || option.showNewLabel);
  const badgeElements = isUnselected
    ? undefined
    : badges?.map(({ variant, text, tooltip }, i) => {
        const badge = <Badge variant={variant}>{text}</Badge>;
        if (!tooltip) {
          return <span key={i}>{badge}</span>;
        }
        return (
          <span key={i} data-qtip-text={tooltip} data-qtip-testid={`${testId}BadgeTooltip-${option.key}-${i}`}>
            {badge}
          </span>
        );
      });
  const badgeRow = badgeElements && badgeElements.length > 0 && (
    <div className="cursorPointer tw:mt-1 tw:flex tw:flex-row tw:flex-wrap tw:gap-1">{badgeElements}</div>
  );

  return (
    <tr
      className={clsx(
        OUTPUT_SECTION_BORDER_STYLES,
        'tw:border-t',
        isUnselected && 'tw:bg-sq-disabled-gray tw:dark:bg-sq-dark-disabled-gray',
      )}>
      {selectable && (
        <td className="tw:p-0!">
          <div className="tw:px-[5px] tw:py-[5px]">
            <Checkbox
              id={`${testId ?? 'formulaItemTable'}Checkbox-${option.key}`}
              isChecked={isSelected}
              onChange={(e) => onSelectionChange?.(option.key, e.target.checked)}
            />
          </div>
        </td>
      )}
      <td colSpan={1} className="tw:p-0!">
        <div className={clsx('tw:relative tw:py-[5px]', selectable ? 'tw:pr-[10px]' : 'tw:px-[10px]')}>
          <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
              {!textEditable && (
                <span>
                  <span className="tw:font-bold">{t('NAME')}:</span> {option.display}
                  {showNewLabel ? <Badge extraClassNames="tw:ml-[7px] text-bold">{newLabel}</Badge> : null}
                </span>
              )}
              {textEditable && onTextChange && (
                <span className="tw:flex tw:flex-row tw:items-center tw:gap-1">
                  <span className="tw:font-bold">{t('NAME')}:</span>
                  <EditableText
                    value={option.display}
                    onUpdate={(newText) => onTextChange(newText, option)}
                    textClasses="tw:inline-block"
                  />
                  {showNewLabel ? <Badge extraClassNames="tw:ml-[7px] text-bold">{newLabel}</Badge> : null}
                </span>
              )}
              {isTemplateDerivedFeatureOption(option) && (
                <OutputItemMetadataLabel
                  outputItemType={option.outputItemType}
                  outputItemMetadata={option.outputItemMetadata}
                />
              )}
              {badgeRow}
            </div>
            <div className="tw:flex tw:flex-row tw:items-center">
              {option.allFormulas && (
                <Icon icon="fc fc-formula" onClick={onToggleFormula} testId={`${testId}FormulaIcon-${option.key}`} />
              )}
              {onEditClick && (
                <Icon
                  icon={faPencil}
                  onClick={() => onEditClick(option.key)}
                  testId={`${testId}EditIcon-${option.key}`}
                  extraClassNames="tw:ml-[8px]"
                />
              )}
              {onAdditionalIconClick && additionalIcon && (
                <Icon
                  icon={additionalIcon}
                  onClick={() => onAdditionalIconClick(option.key)}
                  testId={`${testId}ExtraIcon-${option.key}`}
                  extraClassNames="tw:ml-[8px]"
                  tooltip={additionalIconTooltip && t(additionalIconTooltip)}
                />
              )}
              {onSecondaryIconClick && secondaryIcon && (
                <Icon
                  icon={secondaryIcon}
                  onClick={() => onSecondaryIconClick(option.key)}
                  testId={`${testId}SecondaryExtraIcon-${option.key}`}
                  extraClassNames="tw:ml-[8px]"
                  tooltip={secondaryIconTooltip && t(secondaryIconTooltip)}
                />
              )}
              {onRemoveClick && (
                <Icon
                  icon={faTrash}
                  onClick={() => onRemoveClick(option.key)}
                  testId={`${testId}RemoveIcon-${option.key}`}
                  extraClassNames="tw:ml-[8px]"
                />
              )}
            </div>
          </div>
          {option.isNew && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
            <div className="tw:mt-sq-10 tw:mb-2">
              <Checkbox
                isChecked={option.isMapped ?? false}
                onChange={(e) => mappingConfig.onMappingChange?.(option.key, e.target.checked, undefined)}
                label={t('TEMPLATES.MAP_TO_EXISTING_CHECKBOX_LABEL')}
                id={`${option.key}-mapping-checkbox`}
              />
            </div>
          )}
          {option.isNew && option.isMapped && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
            <div className="tw:mb-2">
              {mappingConfig && mappingConfig.label && mappingConfig.labelTooltip && (
                <div className="flexColumnContainer tw:mb-sq-5 tw:items-center" data-testid={`${testId}MappingLabel`}>
                  <span className={clsx('tw:mr-sq-5')}>{t(mappingConfig?.label)}</span>
                  <Icon
                    icon={faQuestionCircle}
                    large={true}
                    tooltip={t(mappingConfig?.labelTooltip)}
                    testId={`${testId}MappingLabelIcon`}
                    tooltipPlacement="right"
                    extraClassNames="tw:cursor-default"
                  />
                </div>
              )}
              <IconSelect
                testId={`${testId}MappingSelect-${option.key}`}
                selectOptions={mappingConfig?.mappingOptions}
                placeholder={mappingConfig?.mappingPlaceholder}
                value={option.mappedFeatureId}
                onChange={(selected) => mappingConfig.onMappingChange?.(option.key, true, selected.value as string)}
              />
            </div>
          )}
          {isOpen && option.allFormulas && (
            <div>
              {option.allFormulas.map((formula, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    idx === 0 && 'tw:mt-[5px]',
                    idx !== option.allFormulas!.length - 1 && 'tw:mb-[12px]',
                  )}>
                  <span className="tw:font-bold">
                    {t(
                      option.allFormulas!.length > 1 ? 'FORMULA.FORMULA_COUNT' : 'FORMULA.FORMULA',
                      option.allFormulas!.length > 1 ? { count: idx + 1 } : undefined,
                    )}
                    :
                  </span>{' '}
                  <FormulaEditorAsync
                    formula={formula.formula}
                    readOnly={true}
                    readOnlyWithErrors={true}
                    showLineNumbers={false}
                    lineWrapping={true}
                    formulaErrors={formula.formulaErrors}
                  />
                  {formula.parameters && formula.parameters.length > 0 && (
                    <div>
                      <span className="tw:mt-[5px] tw:font-bold">{t('FORMULA.DOCUMENTATION.PARAMETERS')}: </span>{' '}
                      {formula.parameters.map((param) => `${param.identifier} (${param.name})`).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};
